import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import styles from './RichText.module.scss';
import 'react-quill/dist/quill.snow.css';
import { Editor } from 'react-draft-wysiwyg';
import '../../../../node_modules/react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import {
  ContentState,
  convertFromHTML,
  convertToRaw,
  EditorState,
  Modifier,
  BlockMapBuilder,
} from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import { uploadImage } from 'app/utils/imageUploader';
import htmlToDraft from 'html-to-draftjs';
import { handleNewLine } from 'draftjs-utils';
import { checkURL } from './Link';
import { getValidUrl } from 'app/utils/stringMethods';

interface Props {
  value?: string;
  onChange?: (value: string) => void;
  label?: string;
  error?: { [key: string]: any } | any;
  name: string;
  onBlur?: (outputText: string) => void;
  required?: boolean;
}

const uploadCallback = async (file: any) => {
  const imgUrl = await uploadImage(file);
  return new Promise((resolve) => {
    resolve({ data: { link: imgUrl?.data || '' } });
  });
};
const RichText: FunctionComponent<Props> = ({
  value,
  onChange,
  name,
  error,
  label,
  onBlur,
  required,
}) => {
  const editorRef = useRef<any>(null);

  const [editorState, setEditorState] = useState(() =>
    value
      ? EditorState.createWithContent(
          ContentState.createFromBlockArray(
            htmlToDraft(value || '').contentBlocks,
          ),
        )
      : EditorState.createEmpty(),
  );
  function focusEditor() {
    editorRef?.current?.focus?.();
  }

  useEffect(() => {
    focusEditor();
  }, []);
  const handleReturn = (event: any) => {
    // Your handleNewLine function imported from this package
    const newEditorState = handleNewLine(editorState, event);
    if (newEditorState) {
      setEditorState(newEditorState);
      return true;
    }

    // Maintain content of the "data" property of the current block when splitting to create the
    // new block (instead of draftjs normal behavior of not maintaining any part of data).
    // This is adapted from https://github.com/facebook/draft-js/issues/723#issuecomment-367918580
    const contentState = editorState.getCurrentContent();
    const selection = editorState.getSelection();
    const startKey = selection.getStartKey();
    const currentBlock = contentState.getBlockForKey(startKey);
    if (currentBlock) {
      const blockData = currentBlock.getData();
      if (blockData) {
        const newContentState = Modifier.splitBlock(contentState, selection);
        const splitState = EditorState.push(
          editorState,
          newContentState,
          'split-block',
        );
        const splitStateWithData = Modifier.mergeBlockData(
          splitState.getCurrentContent(),
          splitState.getSelection(),
          blockData,
        );
        const finalState = EditorState.push(
          editorState,
          splitStateWithData,
          'split-block',
        );
        setEditorState(finalState);
        return true;
      }
    }
    return false;
  };

  function linkCallback(url: any = {}) {
    const inputField = document.getElementById('linkTarget');
    if (inputField) {
      if (checkURL(url?.target)) {
        inputField.style.border = '1px solid #ffffff87f';
        return url?.target?.includes('//')
          ? url
          : { ...url, target: getValidUrl(url?.target) };
      } else {
        inputField.style.border = '1px solid red';
      }
    }
  }

  return (
    <div className={styles.RichText}>
      <div className={styles.label_required}>
        {label && (
          <label className={error ? styles.LabelError : ''} style={{ gap: 10 }}>
            {label} {required && '*'}
          </label>
        )}
        {error && (
          <p data-testid={`${name}-error-testid`}>
            {typeof error == 'object' ? error[name]?.message : error}
          </p>
        )}
      </div>
      {/* <ReactQuill
        theme="snow"
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        modules={modules}
        formats={formats}
      /> */}
      <>
        <style>
          @import
          url(`https://fonts.googleapis.com/css2?family=Mulish&display=swap`);
          url(`https://fonts.googleapis.com/css2?family=Changa&display=swap`);
        </style>
        <Editor
          // blockStyleFn={myBlockStyleFn}
          toolbar={{
            textAlign: {
              inDropdown: true,
              className: undefined,
              component: undefined,
              dropdownClassName: undefined,
              options: ['left', 'center', 'right', 'justify'],
            },
            link: {
              inDropdown: true,
              linkCallback: linkCallback,
            },

            options: [
              'inline',
              'fontFamily',
              'fontSize',
              'list',
              'textAlign',
              'link',
              'colorPicker',
              'image',
            ],

            inline: {
              options: ['bold', 'italic', 'underline', 'strikethrough'],
            },
            list: {
              options: ['unordered', 'ordered'],
            },
            fontSize: {
              options: [
                8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96,
              ],
            },
            fontFamily: {
              options: ['Changa', 'Mulish'],
            },
            image: {
              // icon: image,
              className: undefined,
              component: undefined,
              popupClassName: undefined,
              urlEnabled: false,
              uploadEnabled: true,
              alignmentEnabled: true,
              uploadCallback: uploadCallback,
              previewImage: false,
              inputAccept: 'image/gif,image/jpeg,image/jpg,image/png,image/svg',
              alt: { present: false, mandatory: false },
              defaultSize: {
                height: 'auto',
                width: 'auto',
              },
            },
          }}
          toolbarStyle={{
            border: '1px solid var(--borderColor)',
            backgroundColor: 'var(--bgColor)',
          }}
          editorStyle={{
            minHeight: '150px',
            maxHeight:'300px',
            overflow:'scroll',
            border: '1px solid var(--borderColor)',
            padding: '10px',
            // lineHeight: '1.0',
            backgroundColor: 'var(--bgColor)',
            fontFamily: 'Changa',
          }}
          editorState={editorState}
          onEditorStateChange={(newState) => {
            setEditorState(newState);
          }}
          onBlur={() => {
            onBlur?.(
              draftToHtml(convertToRaw(editorState.getCurrentContent())),
            );
          }}
          onChange={() =>
            onChange?.(
              draftToHtml(convertToRaw(editorState.getCurrentContent())),
            )
          }
          wrapperClassName={'reactDraft'}
          ref={editorRef}
          wrapperId={+name}
          handlePastedText={(
            text: string,
            html: string,
            editorState: EditorState,
            onChange: (editorState: EditorState) => void,
          ) => {
            if (html) {
              const newState = Modifier.replaceWithFragment(
                editorState.getCurrentContent(),
                editorState.getSelection(),
                BlockMapBuilder.createFromArray(
                  convertFromHTML(html || '').contentBlocks,
                ),
              );
              onChange(
                EditorState.push(editorState, newState, 'insert-fragment'),
              );
              return true;
            }
            return false;
          }}
          handleReturn={handleReturn}
        />
      </>
    </div>
  );
};

export default RichText;
